import { Form, Formik } from "formik";
import { FaPlus } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { Box } from "../Box";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { H2, H3 } from "../Heading";
import { Loader } from "../Loader";
import { AddEditAdditionalDocument } from "../Modals/AddEditAdditionalDocument";
import { Paragraph } from "../Paragraph";
import { useState, useEffect, useRef, useMemo } from "react";
import { useMandateTypeAdditionalDoc } from "../../data/useMandateTypeAdditionalDoc";
import { useUpdateMandateType } from "../../data/useUpdateMandateType";
import { queryClient } from "../../queryClient";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { Toggle } from "../Toggle";
import { useDirtyForm } from "../../DirtyFormContext";
import { useMandateType } from "../../data/useMandateType";
import { useChecks } from "../../data/useChecks";

const arraysEqual = (a: number[], b: number[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const RequestAdditionalDocument = ({ onAdditionalDocumentChange }) => {
  const params = useParams();
  const { setFormDirty, formRef } = useDirtyForm();
  const [selectedMandateTypeId, setSelectedMandateTypeId] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (params.id) {
      const id = parseInt(params.id as string, 10);
      setSelectedMandateTypeId(id);
    }
  }, [params.id]);

  const { data: mandateTypeQuery } = useMandateType(selectedMandateTypeId);

  const selectedType = useMemo(() => {
    const t =
      (mandateTypeQuery as any)?.data?._type ??
      (mandateTypeQuery as any)?._type ??
      undefined;
    return t as "individual" | "entity" | undefined;
  }, [mandateTypeQuery]);

  const { data } = useMandateTypeAdditionalDoc(
    selectedMandateTypeId,
    selectedType
  );

  const { data: checkListQuery } = useChecks(selectedType);
  const checkList = checkListQuery?.data;

  const { mutate: updateMandateType } = useUpdateMandateType();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null
  );
  const [additionalDocumentArray, setAdditionalDocumentArray] = useState<
    number[]
  >([]);

  const cbRef = useRef(onAdditionalDocumentChange);
  useEffect(() => {
    cbRef.current = onAdditionalDocumentChange;
  }, [onAdditionalDocumentChange]);

  useEffect(() => {
    if (!data) return;

    const initiallyAddedDocuments = data
      .filter((doc) => doc.is_added === true)
      .map((doc) => doc.id)
      .sort((a, b) => a - b);

    setAdditionalDocumentArray((prev) => {
      const prevSorted = [...prev].sort((a, b) => a - b);
      if (arraysEqual(prevSorted, initiallyAddedDocuments)) return prev;
      return initiallyAddedDocuments;
    });

    const fullDocs = data.filter((doc) =>
      initiallyAddedDocuments.includes(doc.id)
    );
    cbRef.current?.(initiallyAddedDocuments, fullDocs);
  }, [data]);

  const addedDocumentIds = data
    ? data.filter((doc) => doc.is_added === true).map((doc) => doc.id)
    : [];

  const getInitialValues = () => {
    if (!data) return {};
    const values = formRef.current?.values || {};
    const activeDocumentIds = Object.keys(values)
      .filter((key) => key.startsWith("active_") && values[key])
      .map((key) => parseInt(key.replace("active_", ""), 10));

    const initialValues: Record<string, boolean> = {};
    data.forEach((item) => {
      initialValues[`active_${item.id}`] =
        activeDocumentIds.includes(item.id) || item.is_added || false;
    });
    return initialValues;
  };

  const getCheckpointUpdate = (updatedDocs: number[]) => {
    const currentCheckpoints =
      mandateTypeQuery?.data?.checkpoint?.map((cp: any) => cp.id) || [];

    const individualCheckName = "additional_document_check";
    const entityCheckName = "entity_additional_document_check";
    const requiredCheckName =
      selectedType === "entity" ? entityCheckName : individualCheckName;

    const additionalDocumentCheckId = checkList?.find(
      (check: any) => check.name === requiredCheckName
    )?.id;

    if (!additionalDocumentCheckId) {
      return currentCheckpoints;
    }

    if (updatedDocs.length > 0) {
      return Array.from(
        new Set([...currentCheckpoints, additionalDocumentCheckId])
      );
    }

    return currentCheckpoints.filter(
      (id: number) => id !== additionalDocumentCheckId
    );
  };

  const getCurrentForms = () =>
    (mandateTypeQuery?.data?._form || []).map((form: any, index: number) => ({
      _form:
        typeof form._form === "number" ? form._form : form._form?.id,
      order: form.order ?? index,
    }));

  const handleActiveChange = (id: number, is_added: boolean) => {
    let updatedArray = [...additionalDocumentArray];

    if (is_added) {
      if (!updatedArray.includes(id)) updatedArray.push(id);
    } else {
      updatedArray = updatedArray.filter((docId) => docId !== id);
    }

    setAdditionalDocumentArray(updatedArray);

    const updatedDocs = (data ?? []).filter((doc) =>
      updatedArray.includes(doc.id)
    );
    cbRef.current?.(updatedArray, updatedDocs);
    setFormDirty(true);

    const newCheckpointArray = getCheckpointUpdate(updatedArray);

    updateMandateType(
      {
        params: {
          id: selectedMandateTypeId,
          _form: getCurrentForms(),
          additional_document: updatedArray,
          checkpoint: newCheckpointArray,
        },
        id: selectedMandateTypeId,
      },
      {
        onSuccess: (res) => {
          if (res?.status?.toString().startsWith("2")) {
            queryClient.invalidateQueries([
              "mandateTypeAdditionalDoc",
              selectedMandateTypeId,
              selectedType,
            ]);
            queryClient.invalidateQueries(["mandate", selectedMandateTypeId]);
            notify(
              is_added
                ? "Document added successfully!"
                : "Document removed successfully!",
              { type: "success" }
            );
            setFormDirty(false);
          }
        },
        onError: (err) => showError(err, "Error occurred while updating"),
      }
    );
  };

  const addAdditionalDoc = () => {
    setIsEditModalOpen(true);
    setSelectedDocumentId(null);
  };

  const handleClose = () => setIsEditModalOpen(false);

  const handleDocumentSuccess = (id: number) => {
    const next = [...addedDocumentIds, id];
    const newCheckpointArray = getCheckpointUpdate(next);

    updateMandateType(
      {
        params: {
          id: selectedMandateTypeId,
          _form: getCurrentForms(),
          additional_document: next,
          checkpoint: newCheckpointArray,
        },
        id: selectedMandateTypeId,
      },
      {
        onSuccess: (res) => {
          if (res?.status?.toString().startsWith("2")) {
            queryClient.invalidateQueries([
              "mandateTypeAdditionalDoc",
              selectedMandateTypeId,
              selectedType,
            ]);
            notify("Document added successfully!", { type: "success" });
            setFormDirty(false);
          }
        },
        onError: (err) => showError(err, "Error occured while updating"),
      }
    );

    const updatedDocs = (data ?? []).filter((doc) => next.includes(doc.id));
    cbRef.current?.(next, updatedDocs);
  };

  return (
    <>
      <Box pt={2} width={"100%"}>
        <Flex justifyContent="space-between" alignItems="center">
          <H2 fontSize={3} fontWeight={500}>
            Requests additional documents (optional)
          </H2>
          <Button
            Icon={FaPlus}
            iconSize="16px"
            onClick={addAdditionalDoc}
            variant="primary"
            type="button"
            fontWeight={"bold"}
          >
            Add additional document
          </Button>
        </Flex>

        {!data || data.length === 0 ? (
          <Flex justifyContent="center" alignItems="center" minHeight="25vh">
            <Loader />
          </Flex>
        ) : (
          <Formik
            initialValues={getInitialValues()}
            enableReinitialize
            validate={() => null}
            onSubmit={(values, actions) => {
              setFormDirty(false);
              actions.setSubmitting(false);
            }}
            innerRef={formRef}
          >
            {(props) => (
              <Form onSubmit={props.handleSubmit}>
                <Box>
                  {data?.map((item) => (
                    <Flex
                      key={item.id}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      borderBottom={"1px solid"}
                      borderBottomColor="gray.10"
                      py={2}
                    >
                      <Box>
                        <H3 fontSize={2} mb={"0px"}>
                          {item.name}
                        </H3>
                        <Paragraph my={0} color={"gray.60"}>
                          {item.description}
                        </Paragraph>
                      </Box>
                      <Box>
                        <Toggle
                          isChecked={props.values[`active_${item.id}`] || false}
                          onChange={(e) => {
                            props.setFieldValue(
                              `active_${item.id}`,
                              e.target.checked
                            );
                            handleActiveChange(item.id, e.target.checked);
                          }}
                          mt={2}
                        />
                      </Box>
                    </Flex>
                  ))}
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Box>

      <AddEditAdditionalDocument
        isOpen={isEditModalOpen}
        onRequestClose={handleClose}
        documentId={selectedDocumentId}
        fromMandateType={true}
        onSuccess={handleDocumentSuccess}
      />
    </>
  );
};

export default RequestAdditionalDocument;
